// THE JOIN BEHIND /inventory: catalog identity + reported quantity.
//
// PURE, and deliberately so. The backend keeps aggregate_by_product and
// breakdown_by_product as plain functions over what a provider reported, for
// the stated reason that the absent-is-not-zero rule can then be tested
// directly rather than inferred from an endpoint's output. This is the same
// decision one layer further out: the rule is asserted here, on data, not on
// whatever a virtualized table happened to paint.
//
// ⚠️ THREE STATES, AND THE MIDDLE ONE IS THE POINT.
//
//   qtyOnHand === undefined   UNREPORTED. Nobody looked. The screen says so
//                             and prints no quantity.
//   qtyOnHand === 0           REPORTED AS NONE. Somebody looked and there is
//                             none. A real statement, and the screen may say
//                             "none" on the strength of it.
//   qtyOnHand === n           the ordinary case.
//
// Collapsing the first into the second prints "out of stock" for a warehouse
// nobody has connected yet, and someone declines an order over it. Nothing
// below fills a missing id in, and nothing converts a stated 0 to an absence.
//
// ⚠️ NO COST, NO VALUE, NO ALLOCATION FIELDS -- PERMANENTLY. The seam reports
// product_id, location and a quantity, and that is the whole vocabulary. See
// the module docstring on addons/anixify_cpq/models/stock.py: a cost figure
// here would sit one join from a quote line, which is exactly what the pricing
// fence exists to prevent. A grep test pins this from the frontend side too.

import type { CatalogProduct } from "@/types";
import type { StockLocationLevel } from "@/services/api";

export interface InventoryRow {
  /** React key and selection identity. Built from the product ID and the
   *  level's position, never from the part number -- part numbers are not
   *  unique on the real master, and two products sharing one must stay two
   *  rows rather than merging into a single quantity. */
  key: string;
  product: CatalogProduct;
  /** ⚠️ NULL IS A REAL ANSWER: "not attributed to a location", which is what
   *  a company-wide figure is. It is deliberately not "ALL" or "" -- either
   *  would sort, filter and group as though it were a real warehouse. */
  location: string | null;
  /** ⚠️ undefined means UNREPORTED. Never coerce it. A stated 0 is a fact. */
  qtyOnHand: number | undefined;
  /** When the source read this figure, if it said. Null through the mock
   *  adapter by design -- stamping "now" on a fixture would dress it up as a
   *  fresh reading. */
  asOf: string | null;
  /** Who said it. Descriptive; nothing matches on it. */
  source: string | null;
}

/** Build the screen's rows from the catalog page and the stock read.
 *
 *  One row per (product, reported location). A product nobody reported on
 *  gets ONE row carrying an undefined quantity and no location -- it is still
 *  shown, because "this system has been told nothing about it" is exactly the
 *  fact a stock-on-hand screen exists to surface.
 *
 *  `stock === undefined` means the read FAILED or has not happened. Every
 *  product is then unreported, which is honest; an empty Map would instead
 *  claim every product was checked and found empty. */
export function buildInventoryRows(
  products: CatalogProduct[],
  stock: Map<number, number> | undefined,
  locations: Map<number, StockLocationLevel[]> | undefined,
): InventoryRow[] {
  const rows: InventoryRow[] = [];

  for (const product of products) {
    // ⚠️ `.has` BEFORE `.get`, always. This is what separates "reported 0"
    // from "not mentioned"; `stock?.get(id)` alone would be correct today only
    // by accident of Map returning undefined, and would break the moment
    // anyone "helpfully" added a `?? 0`.
    const reported = stock !== undefined && stock.has(product.id);

    if (!reported) {
      rows.push({
        key: `${product.id}#none`,
        product,
        location: null,
        qtyOnHand: undefined,
        asOf: null,
        source: null,
      });
      continue;
    }

    const levels = locations?.get(product.id);

    // The breakdown was not asked for, or the source gave none: fall back to
    // the aggregate, with NO location. Naming one would attribute a sum across
    // places to a single place.
    if (!levels || levels.length === 0) {
      rows.push({
        key: `${product.id}#agg`,
        product,
        location: null,
        qtyOnHand: stock.get(product.id),
        asOf: null,
        source: null,
      });
      continue;
    }

    // ⚠️ EVERY LEVEL SURVIVES, INCLUDING ONE REPORTING ZERO. A location
    // somebody looked at and found empty is a fact, not noise; dropping it
    // would silently turn "we checked Bengaluru, it has none" into "we have
    // never checked Bengaluru".
    levels.forEach((level, index) => {
      rows.push({
        // The index is load-bearing: two levels can share a null location,
        // and a key built from the location alone would collide.
        key: `${product.id}#${index}`,
        product,
        location: level.location,
        qtyOnHand: level.qty_available,
        asOf: level.as_of,
        source: level.source,
      });
    });
  }

  return rows;
}

/** What is KNOWN, counted per product rather than per row.
 *
 *  Three figures, never two. Folding the unreported into "0 in stock" is the
 *  absent-is-not-zero bug wearing a summary statistic. */
export function summariseInventory(rows: InventoryRow[]): {
  reported: number;
  reportedNone: number;
  unreported: number;
} {
  // Per product: a product reported at two locations is one product, and its
  // quantity for this purpose is the sum of what was reported about it.
  const byProduct = new Map<number, number | undefined>();
  for (const row of rows) {
    const seen = byProduct.get(row.product.id);
    if (row.qtyOnHand === undefined) {
      if (!byProduct.has(row.product.id)) byProduct.set(row.product.id, undefined);
      continue;
    }
    byProduct.set(row.product.id, (seen ?? 0) + row.qtyOnHand);
  }

  let reported = 0;
  let reportedNone = 0;
  let unreported = 0;
  for (const qty of byProduct.values()) {
    if (qty === undefined) unreported++;
    else if (qty === 0) reportedNone++;
    else reported++;
  }
  return { reported, reportedNone, unreported };
}
